import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Simplified recording hook focused on core timing functionality
 * Removed excessive logging and complex state synchronization
 */
export const useRecording = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [startDateTime, setStartDateTime] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalElapsedTime, setTotalElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState(null);
  
  // Internal state for timing calculations
  const startTimeRef = useRef(null);
  const totalElapsedRef = useRef(0);
  const timerRef = useRef(null);
  
  // Update timer display during active recording
  const updateTimer = useCallback(() => {
    if (startTimeRef.current) {
      const elapsed = Date.now() - startTimeRef.current;
      const totalMs = totalElapsedRef.current + elapsed;
      setCurrentTime(Math.floor(totalMs / 1000));
    }
  }, []);
  
  // Timer effect for active recording
  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(updateTimer, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRecording, isPaused, updateTimer]);

  const start = useCallback(() => {
    if (!startDateTime) {
      setStartDateTime(new Date());
    }
    
    const now = Date.now();
    setIsRecording(true);
    setIsPaused(false);
    setStartTime(now);
    startTimeRef.current = now;
  }, [startDateTime]);

  const pause = useCallback(() => {
    if (!isRecording || isPaused) return;
    
    if (startTimeRef.current) {
      const elapsed = Date.now() - startTimeRef.current;
      totalElapsedRef.current += elapsed;
      setTotalElapsedTime(totalElapsedRef.current);
      setCurrentTime(Math.floor(totalElapsedRef.current / 1000));
    }
    
    setIsPaused(true);
    setIsRecording(false);
    setStartTime(null);
    startTimeRef.current = null;
  }, [isRecording, isPaused]);

  const resume = useCallback(() => {
    if (!isPaused) return;
    
    const now = Date.now();
    setIsPaused(false);
    setIsRecording(true);
    setStartTime(now);
    startTimeRef.current = now;
  }, [isPaused]);

  const stop = useCallback(() => {
    if (isRecording && startTimeRef.current) {
      const elapsed = Date.now() - startTimeRef.current;
      totalElapsedRef.current += elapsed;
      setTotalElapsedTime(totalElapsedRef.current);
      setCurrentTime(Math.floor(totalElapsedRef.current / 1000));
    }
    
    setIsRecording(false);
    setIsPaused(false);
    setStartTime(null);
    startTimeRef.current = null;
  }, [isRecording]);

  const reset = useCallback(() => {
    setIsRecording(false);
    setIsPaused(false);
    setStartDateTime(null);
    setCurrentTime(0);
    setTotalElapsedTime(0);
    setStartTime(null);
    startTimeRef.current = null;
    totalElapsedRef.current = 0;
    
    // Clear any running timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const getElapsedTime = useCallback(() => {
    if (isRecording && !isPaused && startTimeRef.current) {
      return totalElapsedRef.current + (Date.now() - startTimeRef.current);
    }
    return totalElapsedRef.current;
  }, [isRecording, isPaused]);

  const setElapsedTime = useCallback((elapsedTimeInSeconds) => {
    const ms = elapsedTimeInSeconds * 1000;
    totalElapsedRef.current = ms;
    setTotalElapsedTime(ms);
    setCurrentTime(elapsedTimeInSeconds);
  }, []);

  const getTotalElapsedSeconds = useCallback(() => {
    return Math.floor(totalElapsedRef.current / 1000);
  }, []);

  // Backward compatibility functions for legacy code
  const setStartDateTimeCompat = useCallback((dateTime) => {
    setStartDateTime(dateTime);
  }, []);

  const setElapsedTimeFromBackend = useCallback((elapsedTimeInSeconds) => {
    const ms = elapsedTimeInSeconds * 1000;
    totalElapsedRef.current = ms;
    setTotalElapsedTime(ms);
    setCurrentTime(elapsedTimeInSeconds);
  }, []);

  const setPausedState = useCallback((elapsedTimeInSeconds = null) => {
    setIsPaused(true);
    setIsRecording(false);
    
    if (elapsedTimeInSeconds !== null && elapsedTimeInSeconds !== undefined) {
      const ms = elapsedTimeInSeconds * 1000;
      totalElapsedRef.current = ms;
      setTotalElapsedTime(ms);
      setCurrentTime(elapsedTimeInSeconds);
    } else {
      const finalTime = Math.floor(totalElapsedRef.current / 1000);
      setCurrentTime(finalTime);
    }
  }, []);

  return {
    // State
    isRecording,
    isPaused,
    startDateTime,
    startTime,
    currentTime,
    totalElapsedTime,
    
    // Actions
    start,
    pause,
    resume,
    stop,
    reset,
    
    // Utilities
    getElapsedTime,
    getTotalElapsedSeconds,
    setElapsedTime,
    
    // Backward compatibility
    setStartDateTime: setStartDateTimeCompat,
    setElapsedTimeFromBackend,
    setPausedState,
    setTotalElapsedTime,
    setCurrentTime
  };
};